import {
  InterventionCategory,
  Intervention,
  PathwayIntervention,
  SubSector,
  Suggestion,
} from '@/types';
import { useState, useCallback } from 'react';
import { identify } from '@/data/identify';
import { levelAndAbove } from '@/data/interventions';
import { noop } from 'lodash/fp';
import {
  Heading,
  Container,
  Frame,
  Content,
  Stack,
  Inline,
  InlineItem,
  Button,
  Menu,
  MenuItem,
} from '@kinesis/bungle';
import { Layout } from '@/components/layout';
import { InterventionDetail } from '@/components/intervention-detail';

const blank = {
  name: '',
  description: '',
  type: undefined,
};

const InterventionNew = (props: InterventionNewProps) => {
  const {
    suggestions = [],
    category,
    onCreate = noop,
    multiTag = false,
    subSectors,
  } = props;
  // New-model pathway interventions carry the explicit tag set; a blank
  // (and an applied suggestion, which has no tag of its own) starts on
  // every evaluation — the level-plus-above default from `current`, so an
  // untouched intervention behaves exactly as a legacy current-level one.
  const blankFor = useCallback(
    () =>
      identify(multiTag ? { ...blank, tags: levelAndAbove('current') } : blank),
    [multiTag],
  );
  const [intervention, setIntervention] = useState<
    Intervention | PathwayIntervention | IncompleteIntervention
  >(blankFor());
  const onReset = useCallback(() => {
    setIntervention(blankFor());
  }, [setIntervention, blankFor]);
  const onAddIntervention = useCallback(() => {
    // FIX validate intervention is complete
    onCreate(intervention as Intervention);
  }, [intervention, onCreate]);

  return (
    <Content padding='none' height='100%'>
      <Container>
        <Frame sizing='fill-container'>
          <Layout>
            <Stack space='large'>
              <InterventionDetail
                intervention={intervention as Intervention}
                onUpdate={setIntervention as any}
                category={category}
                allowTypeChange
                autoFocus
                subSectors={subSectors}
              />
              <Inline>
                <InlineItem sizing='fill-container' />
                <InlineItem sizing='fit-content'>
                  <Button variant='secondary' onClick={onReset}>
                    Reset
                  </Button>
                </InlineItem>
                <InlineItem sizing='fit-content'>
                  <Button variant='primary' onClick={onAddIntervention}>
                    Add intervention
                  </Button>
                </InlineItem>
              </Inline>
            </Stack>
          </Layout>
        </Frame>
        {suggestions.length > 0 && (
          <Frame sizing='fit-content'>
            <Content padding='medium' width={320} height='100%' borderLeft>
              <Stack space='medium'>
                <Heading as='h4' size='small'>
                  suggestions
                </Heading>
                <Menu>
                  {suggestions
                    .filter((s: any) => s.area === category)
                    .map((s: any) => (
                      <MenuItem
                        icon='bulb'
                        content={s.name}
                        key={s.name}
                        onClick={() =>
                          setIntervention(
                            identify(
                              multiTag
                                ? { tags: levelAndAbove('current'), ...s }
                                : { tag: 'current', ...s },
                            ),
                          )
                        }
                      />
                    ))}
                </Menu>
              </Stack>
            </Content>
          </Frame>
        )}
      </Container>
    </Content>
  );
};

export type IncompleteIntervention = {
  name: string;
  description: string;
  type: undefined;
};

export type InterventionNewProps = {
  suggestions?: Suggestion[];
  category: InterventionCategory;
  onCreate?: (intervention: Intervention | PathwayIntervention) => void;
  // New-model pathway surface: the intervention carries a tag *set*
  // (`tags`) — blanks and applied suggestions start on every evaluation.
  multiTag?: boolean;
  // Passed through to the targeting options (see `InterventionDetail`).
  subSectors?: SubSector[];
};

export { InterventionNew };
